// EditorModal — thin wrapper around `Modal` that requires a `dirty: boolean`
// prop and auto-wires `useUnsavedChangesGuard` so future entity editors cannot
// forget to register their dirty tracking.
//
// @aiPurpose Architecturally enforce the unsaved-changes guard on every
//   editable modal surface by making `dirty` a required prop and routing
//   the underlying `Modal.onClose` through `requestClose`.
// @aiInvariant Composes the existing `Modal` primitive; does NOT duplicate
//   its implementation. The hook owns close-path arbitration; the wrapper
//   merely funnels props.
import { useMemo, type ReactNode } from 'react';
import { Modal, type ModalProps } from './Modal.js';
import { useUnsavedChangesGuard } from './useUnsavedChangesGuard.js';
import { EditorGuardContext, type EditorGuardValue } from './useEditorGuard.js';

export interface EditorModalProps extends Omit<ModalProps, 'onClose' | 'dismissDisabled'> {
  /** True when the surface has meaningful unsaved changes vs its baseline. */
  dirty: boolean;
  /** True while a save/create/reset/delete/upload mutation owned by the surface is in flight. */
  busy?: boolean;
  /** Called when the user confirms discard or when the surface is clean. */
  onConfirmClose: () => void;
  children: ReactNode;
}

export function EditorModal({
  dirty,
  busy = false,
  onConfirmClose,
  children,
  ...rest
}: EditorModalProps) {
  const { requestClose, confirmDestructive, bypassNext, effectiveDismissDisabled } =
    useUnsavedChangesGuard({ dirty, busy, onConfirmClose });

  const guardValue = useMemo<EditorGuardValue>(
    () => ({ confirmDestructive, bypassNext }),
    [confirmDestructive, bypassNext],
  );

  // WHY (#3039): the provider wraps the whole `Modal`, not just its children,
  // so a footer action (`Modal.footer` — the natural home for Cancel/Save on a
  // narrow screen) resolves the SAME guard as the body. Wrapping only
  // `children` makes `useEditorGuard()` throw in the footer, since context
  // follows the render tree.
  return (
    <EditorGuardContext.Provider value={guardValue}>
      <Modal {...rest} onClose={requestClose} dismissDisabled={effectiveDismissDisabled}>
        {children}
      </Modal>
    </EditorGuardContext.Provider>
  );
}
